import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type { PreparedItemDetailsApiResponse } from "@/features/prepared-items/types/preparedItemDetailsApi.types";
import type { PreparedItemDetails } from "@/features/prepared-items/types/preparedItemDetails.types";
import { mapPreparedItemDetailsResponse } from "@/features/prepared-items/utils/preparedItemDetails.utils";
import type { GetPreparedItemsParams, HelperDataResponse, PreparedItemsResponse } from "@/features/prepared-items/types/preparedItems.types";
import type { PreparedItemCreateFormValues, PreparedItemsCreateResponse } from "@/features/prepared-items/types/preparedItemCreate.types";
import type { PreparedItemsDraftResponse } from "@/features/prepared-items/types/preparedItems.draft,types";
import type { DraftResponse, ImportItemsResponse } from "@/app/types/items.types";
import type { PreparedItemImportRequest } from "@/features/prepared-items/types/preparedItemCreate.types";

export type ChangePreparedItemStatusRequest = {
  item_ids: number[];
  status: "active" | "inactive";
};

export type UpdatePreparedItemMutationArgs = {
  id: string | number;
  data: FormData;
};

export const preparedItemsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreparedItems: builder.query<PreparedItemsResponse, GetPreparedItemsParams>({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sku = "",
        category_ids,
        subcategory_ids,
        status,
        storage_condition_ids,
        output_uom_ids,
        last_update_date_from,
        last_update_date_to,
        is_finished,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("per_page", String(limit));
        queryParams.set("page", String(page));

        if (search) {
          queryParams.set("search", search);
        }
        if (sku) {
          queryParams.set("sku", sku);
        }
        if (status) {
          queryParams.set("status", status);
        }
        const addArrayParams = (key: string, values?: number[]) => {
          if (values && values.length > 0) {
            values.forEach((value, index) => {
              queryParams.append(`${key}[${index}]`, String(value));
            });
          }
        };

        addArrayParams("category_ids", category_ids);
        addArrayParams("subcategory_ids", subcategory_ids);
        addArrayParams("storage_condition_ids", storage_condition_ids);
        addArrayParams("output_uom_ids", output_uom_ids);
        if (last_update_date_from) {
          queryParams.set("last_update_date_from", last_update_date_from);
        }
        if (last_update_date_to) {
          queryParams.set("last_update_date_to", last_update_date_to);
        }
        if (is_finished !== undefined) {
          queryParams.set("is_finished", String(is_finished));
        }

        return `${Endpoints.preparedItems.getAllPreparedItems}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<PreparedItemsResponse>,
      ): PreparedItemsResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load prepared items");
        }

        return response.data;
      },
      providesTags: ["PreparedItems"],
    }),
    getPreparedItemById: builder.query<PreparedItemDetails, string | number>({
      query: (id) => Endpoints.preparedItems.getById(id),
      transformResponse: (
        response: ApiResponse<PreparedItemDetailsApiResponse>,
      ): PreparedItemDetails => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load prepared item details",
          );
        }

        return mapPreparedItemDetailsResponse(response.data);
      },
      providesTags: (_result, _error, id) => [
        { type: "PreparedItem", id: String(id) },
      ],
      keepUnusedDataFor: 0,
    }),

    changePreparedItemStatus: builder.mutation<
      ApiResponse<unknown>,
      ChangePreparedItemStatusRequest
    >({
      query: ({ item_ids, status }) => ({
        url: Endpoints.preparedItems.changeStatus,
        method: "PUT",
        body: { item_ids, status },
      }),
      invalidatesTags: (_result, _error, { item_ids }) => [
        "PreparedItems",
        ...item_ids.map((id) => ({ type: "PreparedItem" as const, id: String(id) })),
      ],
    }),
    getPreparedItemsHelperData: builder.query<HelperDataResponse, void>({
      query: () => Endpoints.preparedItems.getHelperData,
      transformResponse: (
        response: ApiResponse<HelperDataResponse>,
      ): HelperDataResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load helper data");
        }
        return response.data;
      },
      providesTags: [{ type: "PreparedItems", id: "HelperData" }],
    }),
    deletePreparedItems: builder.mutation<void, number[]>({
      query: (itemIds) => {
        const queryParams = new URLSearchParams();
        itemIds.forEach((id, index) => {
          queryParams.append(`item_ids[${index}]`, String(id));
        });
        return {
          url: `${Endpoints.preparedItems.deletePreparedItems}?${queryParams.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PreparedItems"],
    }),
    createPreparedItems: builder.mutation<
      PreparedItemsCreateResponse,
      { data: PreparedItemCreateFormValues }
    >({
      query: ({ data }) => ({
        url: Endpoints.preparedItems.createPreparedItems,
        method: "POST",
        body: data,
      }),
      transformResponse: (
        response: ApiResponse<PreparedItemsCreateResponse>,
      ): PreparedItemsCreateResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to create prepared items");
        }
        return response.data;
      },
      invalidatesTags: (result) => {
        if (result) return [{ type: "PreparedItems", id: "List" }];
        return [];
      },
    }),
    showPreparedItemsDraft: builder.query<ApiResponse<PreparedItemsDraftResponse>, string>({
      query: (id) => `${Endpoints.draft.showPreparedItemsDraft}/${id}`,
      transformResponse: (
        response: ApiResponse<PreparedItemsDraftResponse>,
      ): ApiResponse<PreparedItemsDraftResponse> => {
        return response;
      },
      providesTags: ["PreparedItemsDraft"],
    }),
    updatePreparedItemsDraft: builder.mutation<
      PreparedItemsCreateResponse | DraftResponse,
      { id: string; data: PreparedItemCreateFormValues }
    >({
      query: ({ id, data }) => ({
        url: `${Endpoints.draft.updatePreparedItemsDraft}/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (
        response: ApiResponse<PreparedItemsCreateResponse | DraftResponse>,
      ): PreparedItemsCreateResponse | DraftResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to update prepared items draft");
        }
        return response.data;
      },
      invalidatesTags: (_result, _error, { data }) =>
        data.status === "draft"
          ? ["PreparedItemsDraft", { type: "PreparedItems", id: "List" }]
          : [{ type: "PreparedItems", id: "List" }],
    }),
    importPreparedItems: builder.mutation<ImportItemsResponse, PreparedItemImportRequest>({
      query: (body) => ({
        url: Endpoints.preparedItems.importPreparedItems,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PreparedItems"],
    }),
    updatePreparedItem: builder.mutation<
      ApiResponse<unknown>,
      UpdatePreparedItemMutationArgs
    >({
      query: ({ id, data }) => ({
        url: Endpoints.preparedItems.updatePreparedItem(id),
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "PreparedItems",
        { type: "PreparedItem", id: String(id) },
      ],
    }),
  }),
});

export const {
  useGetPreparedItemByIdQuery,
  useChangePreparedItemStatusMutation,
  useGetPreparedItemsQuery,
  useLazyGetPreparedItemsQuery,
  useGetPreparedItemsHelperDataQuery,
  useDeletePreparedItemsMutation,
  useCreatePreparedItemsMutation,
  useShowPreparedItemsDraftQuery,
  useUpdatePreparedItemsDraftMutation,
  useImportPreparedItemsMutation,
  useUpdatePreparedItemMutation,
} = preparedItemsApi;
